/**
 * Project-config-first modal for new projects.
 *
 * Both creation paths funnel through this dialog:
 *  - "Import a schema file" → file picked → dialog opens with name
 *    pre-filled from schema title.
 *  - "Create with the editor" → dialog opens immediately with empty
 *    schema; user names the project and (optionally) configures deploy.
 *
 * The dialog returns `{ name, deployTarget } | null` via the onConfirm
 * callback; the caller does the actual project creation. Keeps the
 * dialog ignorant of storage and routing.
 */

import type { AssetOriginConfig, DeployTargetConfig } from '@bliksys/shared';
import { useEffect, useId, useRef, useState } from 'react';

import { AssetOriginField, labelAssetKind, validateAssetOrigin } from './AssetOriginField.js';
import { DeployTargetField, labelDeployKind, validateDeployTarget } from './DeployTargetField.js';
import { SettingsSection } from './ProjectSettings.js';

import './ProjectSettings.css';

export interface NewProjectConfig {
  name: string;
  deployTarget: DeployTargetConfig | undefined;
  assetOrigin: AssetOriginConfig | undefined;
}

interface Props {
  /** Title shown at the top of the dialog. */
  title: string;
  /** Body text under the title — explains what's about to happen. */
  description?: string;
  /** Pre-filled name. From the schema's title for imports, blank otherwise. */
  initialName: string;
  /** Confirm button label. */
  confirmLabel: string;
  onConfirm: (config: NewProjectConfig) => void;
  onCancel: () => void;
  /** Whether to expose the inline deploy-target field. Hidden in cloud
   * mode where the org configures targets centrally — the project just
   * picks one from a managed list (separate UI). Defaults to `true`. */
  showDeployTarget?: boolean;
  /** Same as `showDeployTarget` for the asset origin field. */
  showAssetOrigin?: boolean;
}

export function NewProjectDialog({
  title,
  description,
  initialName,
  confirmLabel,
  onConfirm,
  onCancel,
  showDeployTarget = true,
  showAssetOrigin = true,
}: Props): JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const nameId = useId();
  const [name, setName] = useState(initialName);
  const [target, setTarget] = useState<DeployTargetConfig | undefined>(undefined);
  const [origin, setOrigin] = useState<AssetOriginConfig | undefined>(undefined);
  // Both sections start collapsed for a fresh project — common case is
  // "just name it and go". Auto-expand on validation error so a
  // failure mode points at its own field.
  const [deployOpen, setDeployOpen] = useState(false);
  const [assetOpen, setAssetOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (d && !d.open) {
      d.showModal();
      window.setTimeout(() => nameInputRef.current?.focus(), 0);
    }
  }, []);

  // Native dialog Esc → cancel.
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    const handler = (): void => onCancel();
    d.addEventListener('cancel', handler);
    return () => d.removeEventListener('cancel', handler);
  }, [onCancel]);

  const handleConfirm = (): void => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Project name is required.');
      return;
    }
    // Same validation rule as the settings dialog — don't ship a
    // half-configured deploy target with a fresh project.
    const deployErrors = validateDeployTarget(target);
    const firstDeployError = Object.values(deployErrors)[0];
    if (firstDeployError) {
      setDeployOpen(true);
      setError(`Deploy target: ${firstDeployError}`);
      return;
    }
    const originErrors = validateAssetOrigin(origin);
    const firstOriginError = Object.values(originErrors)[0];
    if (firstOriginError) {
      setAssetOpen(true);
      setError(`Asset origin: ${firstOriginError}`);
      return;
    }
    onConfirm({ name: trimmed, deployTarget: target, assetOrigin: origin });
  };

  return (
    <dialog ref={dialogRef} className="project-settings-dialog" aria-labelledby="np-title">
      <form
        method="dialog"
        className="project-settings-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleConfirm();
        }}
      >
        <header className="project-settings-header">
          <h2 id="np-title" className="project-settings-title">
            {title}
          </h2>
          {description && <p className="project-settings-description">{description}</p>}
        </header>

        <div className="project-settings-body">
          <div className="landing-field">
            <label className="landing-label" htmlFor={nameId}>
              Project name
            </label>
            <input
              ref={nameInputRef}
              id={nameId}
              type="text"
              className="input"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              placeholder="e.g. ACME Corp. website"
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          {showDeployTarget && (
            <SettingsSection
              title="Deploy target"
              badge={target ? labelDeployKind(target.kind) : 'None'}
              badgeIsSet={target !== undefined}
              open={deployOpen}
              onToggle={setDeployOpen}
            >
              <DeployTargetField target={target} onChange={setTarget} webhookCloudOnly />
            </SettingsSection>
          )}

          {showAssetOrigin && (
            <SettingsSection
              title="Asset origin"
              badge={origin ? labelAssetKind(origin.kind) : 'None'}
              badgeIsSet={origin !== undefined}
              open={assetOpen}
              onToggle={setAssetOpen}
            >
              <AssetOriginField origin={origin} onChange={setOrigin} />
            </SettingsSection>
          )}

          {error && (
            <p className="project-settings-error" role="alert">
              {error}
            </p>
          )}
        </div>

        <footer className="project-settings-footer">
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            {confirmLabel}
          </button>
        </footer>
      </form>
    </dialog>
  );
}
